/**
 * ConversationRunTaskList
 *
 * Live task list for a single assistant message's run, rendered below the CoT
 * in the conversation. Subscribes to the run's task events via the platform
 * event stream and folds them (both the `task.*` and `agent.run.agent_task_*`
 * vocabularies) into the ordered model, then renders the minimal
 * {@link ConversationTaskList}.
 *
 * Reads {@link PlatformEventContext} directly (not via `usePlatformEvents`) so
 * it degrades to nothing where no provider is mounted (e.g. static showcase
 * pages) instead of throwing. Renders null until at least one task exists, so
 * it has zero visual impact on the (common) task-less message.
 */

import { useContext, useEffect, useState } from 'react';
import { PlatformEventContext } from '../../contexts/PlatformEventContext';
import { ConversationTaskList } from './cot-task-list';
import {
  applyTaskEvent,
  type ConversationTask,
  type ConversationTaskMap,
} from './cot-task-derive';

/**
 * Subscribes to a run's task events and returns the folded, ordered task list.
 * Reads {@link PlatformEventContext} directly so it degrades to an empty list
 * where no provider is mounted (static showcase pages) instead of throwing.
 */
export function useConversationTasks(runId?: string): ConversationTask[] {
  const platform = useContext(PlatformEventContext);
  const subscribe = platform?.subscribe;
  const [tasks, setTasks] = useState<ConversationTask[]>([]);

  useEffect(() => {
    if (!runId || !subscribe) {
      setTasks([]);
      return;
    }

    const map: ConversationTaskMap = new Map();
    const unsubscribe = subscribe(
      [
        { eventType: 'agent.run.agent_task_*', runId },
        { eventType: 'task.*', runId },
      ],
      (event) => {
        applyTaskEvent(map, event);
        setTasks(Array.from(map.values()));
      },
    );
    return unsubscribe;
  }, [runId, subscribe]);

  return tasks;
}

/**
 * Presentational "Tasks" card. Renders nothing when there are no tasks, so it
 * has zero visual impact on the (common) task-less message.
 */
export function ConversationTaskCard({
  tasks,
  className,
}: {
  tasks: ConversationTask[];
  className?: string;
}) {
  if (tasks.length === 0) return null;

  return (
    // Uniform 24px padding (`p-6`). The left inset lines the "Tasks" header and
    // task-row icons up with the CoT timeline icons (which sit at ml-6); the
    // matching top/bottom/right keep the card's inner spacing even. The card's
    // left border stays under the CoT header icon.
    <div className={`rounded-lg border border-border p-6 ${className ?? ''}`}>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Tasks
      </h3>
      <ConversationTaskList tasks={tasks} />
    </div>
  );
}

/** Self-contained live task card for a run — subscription + card. */
export function ConversationRunTaskList({ runId }: { runId?: string }) {
  const tasks = useConversationTasks(runId);
  return <ConversationTaskCard tasks={tasks} className="mt-2" />;
}
